/**
 * AIAssistant.jsx — AI helper panels for CaseMinder
 *
 * SESSION BOUNDARY RULES (enforced Apr 2026 to fix chat bleed between
 * CaseMinder and Bonsai Mail):
 *
 * - CaseMinder AI calls use session_id = `cm_${submissionId}` — scoped
 *   to the specific matter. History stays inside that matter.
 * - Bonsai Mail AI calls use session_id = `mail_${threadId}` — scoped
 *   to the email thread. Never shares history with CaseMinder.
 * - When Bonsai Mail is launched FROM a CaseMinder matter, the matter
 *   reference and key facts are injected as a read-only context_prefix
 *   in the request payload. The Bonsai Mail session still gets its own
 *   isolated session_id — it reads the matter context but does NOT
 *   inherit CaseMinder's conversation history.
 * - BonsaiMate (chat assistant) uses session_id = `mate_${userId}_${product}`
 *   where product is "caseminder" or "bonsaimail" — never shared.
 *
 * Result: no cross-contamination. Email-drafting mode cannot bleed
 * into case-advisory mode and vice versa.
 */

import { useState } from "react";
import { API } from '../config/api';
import axios from "axios";
import { errorMessage } from "../utils/errorMessage";
import { Button } from "./ui/button";
import { 
  Sparkles, 
  Loader2, 
  AlertTriangle, 
  CheckCircle2, 
  XCircle,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  RefreshCw,
  Info
} from "lucide-react";
import { toast } from "sonner";

/**
 * Build a scoped session_id for CaseMinder AI calls.
 * Ensures no history bleeds between matters or products.
 */
const caseMinderSessionId = (submissionId) => `cm_${submissionId}`;

/**
 * Build a scoped session_id for Bonsai Mail AI calls.
 * Pass threadId from the mail add-in context.
 */
export const bonsaiMailSessionId = (threadId) => `mail_${threadId}`;

/**
 * Build a scoped session_id for BonsaiMate chat.
 * product must be "caseminder" or "bonsaimail" — never shared.
 */
export const bonsaiMateSessionId = (userId, product) => `mate_${userId}_${product}`;

/**
 * Build a matter-scoped session_id for BonsaiMate chat when mounted on
 * a matter detail page. One Mate chat per user per matter — chats persist
 * server-side (via /mate/history?session_id=...) so revisiting a matter
 * resumes the prior conversation about it.
 */
export const bonsaiMateMatterSessionId = (userId, matterReference) =>
  `mate_${userId}_matter_${matterReference}`;

/**
 * Build a read-only matter context prefix to inject into Bonsai Mail
 * when launched from a CaseMinder matter. This gives Bonsai Mail the
 * facts it needs without merging the two chat histories.
 */
export const buildMatterContextPrefix = (matterRef, clientName, matterType) =>
  `[READ-ONLY MATTER CONTEXT — do not include in chat history]
   Matter: ${matterRef} | Client: ${clientName} | Type: ${matterType}
   [END CONTEXT]`;

/**
 * AI Summary Panel for Matter Intake Review
 */
export function AIMatterSummary({ submissionId, onGenerate }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [error, setError] = useState(null);

  const generateSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/ai/matter-summary/${submissionId}`, {
        session_id: caseMinderSessionId(submissionId),
      });
      setResult(response.data);
      if (onGenerate) onGenerate(response.data);
    } catch (err) {
      setError(errorMessage(err, "Failed to generate summary"));
      toast.error("AI summary failed");
    } finally {
      setLoading(false);
    }
  };

  const logAction = async (action) => {
    if (result?.log_id) {
      try {
        await axios.post(`${API}/ai/log-action/${result.log_id}`, { action });
      } catch (err) {
        console.error("Failed to log action:", err);
      }
    }
  };

  if (error) {
    return (
      <div className="border border-red-200 rounded-md p-4 bg-status-red-dim">
        <div className="flex items-center gap-2 text-status-red">
          <XCircle className="w-5 h-5" />
          <span className="font-medium">AI Error</span>
        </div>
        <p className="text-sm text-status-red mt-2">{error}</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={generateSummary}>
          <RefreshCw className="w-4 h-4 mr-1" />
          Retry
        </Button>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="border border-purple-200 rounded-md p-4 bg-gradient-to-r from-purple-50 to-indigo-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <span className="font-medium text-purple-800">AI Summary & Checks</span>
          </div>
          <Button
            onClick={generateSummary}
            disabled={loading}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="generate-ai-summary-btn"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analysing...</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-2" />Generate Summary & Checks</>
            )}
          </Button>
        </div>
        <p className="text-sm text-purple-600 mt-2">
          AI will review the matter and highlight key points, risks, and missing information.
        </p>
      </div>
    );
  }

  return (
    <div className="border border-purple-200 rounded-md overflow-hidden bg-white">
      <div
        className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <span className="font-medium text-purple-800">AI Analysis</span>
            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-md">GPT-4o</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); generateSummary(); }} disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            {expanded ? <ChevronUp className="w-5 h-5 text-purple-600" /> : <ChevronDown className="w-5 h-5 text-purple-600" />}
          </div>
        </div>
      </div>
      {expanded && (
        <div className="p-4 space-y-4">
          <div>
            <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
              <Info className="w-4 h-4 text-blue-500" />Matter Summary
            </h4>
            <ul className="space-y-1">
              {result.summary?.map((item, i) => (
                <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                  <span className="text-blue-500 mt-1">•</span>{item}
                </li>
              ))}
            </ul>
          </div>
          {result.risks?.length > 0 && (
            <div>
              <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-status-amber" />Key Risks / Issues
              </h4>
              <ul className="space-y-1">
                {result.risks.map((item, i) => (
                  <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                    <span className="text-status-amber mt-1">•</span>{item}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {result.missing_information?.length > 0 && (
            <div>
              <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                <XCircle className="w-4 h-4 text-status-red" />Missing Information
              </h4>
              <div className="flex flex-wrap gap-2">
                {result.missing_information.map((item, i) => (
                  <span key={i} className="text-xs bg-status-red-dim text-status-red px-2 py-1 rounded-md border border-red-200">{item}</span>
                ))}
              </div>
            </div>
          )}
          <div className="text-xs text-slate-400 pt-2 border-t border-slate-100 flex items-center gap-1">
            <Info className="w-3 h-3" />AI suggestions only – final decisions are made by qualified staff.
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * AI Conflict Explanation Panel
 */
export function AIConflictExplanation({ submissionId, matchedContacts, onExplanation }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const getExplanation = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/ai/conflict-explanation`, {
        submission_id: submissionId,
        matched_contacts: matchedContacts,
        session_id: caseMinderSessionId(submissionId),
      });
      setResult(response.data);
      if (onExplanation) onExplanation(response.data);
    } catch (err) {
      setError(errorMessage(err, "Failed to generate explanation"));
      toast.error("AI explanation failed");
    } finally {
      setLoading(false);
    }
  };

  if (!matchedContacts || matchedContacts.length === 0) return null;

  if (error) {
    return (
      <div className="text-sm text-status-red p-2 bg-status-red-dim rounded">
        {error}<Button variant="link" size="sm" onClick={getExplanation}>Retry</Button>
      </div>
    );
  }

  if (!result) {
    return (
      <Button
        variant="outline" size="sm"
        onClick={getExplanation}
        disabled={loading}
        className="text-purple-600 border-purple-200 hover:bg-purple-50"
        data-testid="ai-explain-conflict-btn"
      >
        {loading ? (
          <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Analysing...</>
        ) : (
          <><Sparkles className="w-4 h-4 mr-1" />Explain</>
        )}
      </Button>
    );
  }

  return (
    <div className="mt-3 p-3 bg-purple-50 rounded-md border border-purple-200">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4 text-purple-600" />
        <span className="text-sm font-medium text-purple-800">AI Explanation</span>
      </div>
      <p className="text-sm text-slate-700 mb-3">{result.explanation}</p>
      {result.suggested_conditions?.length > 0 && (
        <div>
          <p className="text-xs font-medium text-slate-600 mb-1">Suggested conditions:</p>
          <ul className="space-y-1">
            {result.suggested_conditions.map((condition, i) => (
              <li key={i} className="text-xs text-slate-600 flex items-start gap-1">
                <CheckCircle2 className="w-3 h-3 text-status-green mt-0.5 flex-shrink-0" />{condition}
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="text-xs text-slate-400 mt-2 italic">AI suggestion only – review carefully before deciding.</p>
    </div>
  );
}

/**
 * AI CCL Helper Panel
 */
export function AICCLHelper({ submissionId }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState({});
  const [error, setError] = useState(null);

  const getSuggestions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/ai/ccl-suggestions/${submissionId}`, {
        session_id: caseMinderSessionId(submissionId),
      });
      setResult(response.data);
    } catch (err) {
      setError(errorMessage(err, "Failed to generate suggestions"));
      toast.error("AI suggestions failed");
    } finally {
      setLoading(false);
    }
  };

  const copyText = (key, text) => {
    navigator.clipboard.writeText(text);
    setCopied({ ...copied, [key]: true });
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied({ ...copied, [key]: false }), 2000);
  };

  if (error) {
    return (
      <div className="text-sm text-status-red p-3 bg-status-red-dim rounded-md">
        {error}<Button variant="link" size="sm" onClick={getSuggestions}>Retry</Button>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="border border-purple-200 rounded-md p-4 bg-gradient-to-r from-purple-50 to-indigo-50">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              <span className="font-medium text-purple-800">AI Suggestions</span>
            </div>
            <p className="text-sm text-purple-600 mt-1">Get suggested wording for scope, risks, and funding sections.</p>
          </div>
          <Button onClick={getSuggestions} disabled={loading} className="bg-purple-600 hover:bg-purple-700" data-testid="get-ccl-suggestions-btn">
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-2" />Suggest Wording</>
            )}
          </Button>
        </div>
      </div>
    );
  }

  const sections = [
    { key: 'scope', title: 'Scope Clarification', text: result.scope },
    { key: 'risks', title: 'Risk Warnings', text: result.risks },
    { key: 'funding', title: 'Funding/Fees Explanation', text: result.funding },
  ];

  return (
    <div className="border border-purple-200 rounded-md overflow-hidden">
      <div className="p-3 bg-gradient-to-r from-purple-50 to-indigo-50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-600" />
          <span className="font-medium text-purple-800">AI Suggested Wording</span>
        </div>
        <Button variant="ghost" size="sm" onClick={getSuggestions} disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>
      <div className="p-4 space-y-4">
        {sections.map(({ key, title, text }) => (
          text && (
            <div key={key} className="border border-slate-200 rounded-md p-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-slate-700">{title}</h4>
                <Button variant="ghost" size="sm" onClick={() => copyText(key, text)} className="h-7 px-2">
                  {copied[key] ? <Check className="w-4 h-4 text-status-green" /> : <Copy className="w-4 h-4 text-slate-400" />}
                </Button>
              </div>
              <p className="text-sm text-slate-600 whitespace-pre-wrap">{text}</p>
            </div>
          )
        ))}
        <p className="text-xs text-slate-400 italic">Copy and paste as needed. AI suggestions only – review before use.</p>
      </div>
    </div>
  );
}

/**
 * AI Data Quality Check Panel
 */
export function AIDataQualityCheck({ submissionId, onCheck }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const checkData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/ai/lawbillity-check/${submissionId}`, {
        session_id: caseMinderSessionId(submissionId),
      });
      setResult(response.data);
      if (onCheck) onCheck(response.data);
    } catch (err) {
      setError(errorMessage(err, "Failed to check data"));
      toast.error("AI check failed");
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    return (
      <div className="text-sm text-status-red p-2 bg-status-red-dim rounded">
        {error}<Button variant="link" size="sm" onClick={checkData}>Retry</Button>
      </div>
    );
  }

  if (!result) {
    return (
      <Button
        variant="outline" size="sm"
        onClick={checkData}
        disabled={loading}
        className="text-purple-600 border-purple-200 hover:bg-purple-50"
        data-testid="ai-data-check-btn"
      >
        {loading ? (
          <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Checking...</>
        ) : (
          <><Sparkles className="w-4 h-4 mr-1" />AI Data Check</>
        )}
      </Button>
    );
  }

  const hasIssues = result.issues?.length > 0;

  return (
    <div className={`p-3 rounded-md border ${hasIssues ? 'bg-status-amber-dim border-amber-200' : 'bg-status-green-dim border-green-200'}`}>
      <div className="flex items-center gap-2 mb-2">
        {hasIssues ? (
          <><AlertTriangle className="w-5 h-5 text-status-amber" /><span className="font-medium text-status-amber">Data Quality Issues</span></>
        ) : (
          <><CheckCircle2 className="w-5 h-5 text-status-green" /><span className="font-medium text-status-green">Data Looks Good</span></>
        )}
      </div>
      {hasIssues && (
        <ul className="space-y-2">
          {result.issues.map((issue, i) => (
            <li key={i} className="text-sm flex items-start gap-2">
              <span className={issue.severity === 'error' ? 'text-status-red' : 'text-status-amber'}>
                {issue.severity === 'error' ? '⚠' : '•'}
              </span>
              <span className="text-slate-700"><strong>{issue.field}:</strong> {issue.message}</span>
            </li>
          ))}
        </ul>
      )}
      {result.suggested_clean_values && Object.keys(result.suggested_clean_values).length > 0 && (
        <div className="mt-3 pt-3 border-t border-amber-200">
          <p className="text-xs font-medium text-slate-600 mb-1">Suggested corrections:</p>
          {Object.entries(result.suggested_clean_values).map(([field, value]) => (
            <p key={field} className="text-xs text-slate-600"><strong>{field}:</strong> {value}</p>
          ))}
        </div>
      )}
      <p className="text-xs text-slate-400 mt-2 italic">AI check only – verify before proceeding.</p>
    </div>
  );
}

/**
 * Dashboard Insights Panel
 */
export function DashboardInsights() {
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState([]);
  const [error, setError] = useState(null);

  const loadInsights = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/dashboard/insights`);
      setInsights(response.data.insights || []);
    } catch (err) {
      setError("Failed to load insights");
    } finally {
      setLoading(false);
    }
  };

  useState(() => { loadInsights(); }, []);

  if (loading) {
    return <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" />Loading insights...</div>;
  }

  if (error || insights.length === 0) return null;

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'error': return 'bg-status-red-dim border-red-200 text-status-red';
      case 'warning': return 'bg-status-amber-dim border-amber-200 text-status-amber';
      default: return 'bg-blue-50 border-blue-200 text-blue-800';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'error': return <XCircle className="w-5 h-5 text-status-red" />;
      case 'warning': return <AlertTriangle className="w-5 h-5 text-status-amber" />;
      default: return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  return (
    <div className="space-y-3">
      <h3 className="font-medium text-slate-700 flex items-center gap-2">
        <AlertTriangle className="w-5 h-5 text-status-amber" />Housekeeping Alerts
      </h3>
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {insights.map((insight) => (
          <div key={insight.id} className={`p-4 rounded-md border ${getSeverityColor(insight.severity)}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                {getSeverityIcon(insight.severity)}
                <span className="font-medium">{insight.count}</span>
              </div>
            </div>
            <h4 className="font-medium mt-2">{insight.title}</h4>
            <p className="text-sm opacity-80 mt-1">{insight.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default {
  AIMatterSummary,
  AIConflictExplanation,
  AICCLHelper,
  AIDataQualityCheck,
  DashboardInsights
};
